import { useRef, useState } from 'react';
import { errorText } from '~/lib/apiError';
import { toast } from 'react-toastify';
import { Download, RotateCcw, Trash2, Upload } from 'lucide-react';

import { Select, ToggleField, toOptions } from '~/components/form';
import { Badge, Button, ConfirmModal, PanelTable } from '~/components/ui';
import { DOC_TYPES } from '~/components/Constants/mlsAgreementsConstants';
import {
  useDeleteAgreementDocumentMutation,
  useDownloadAgreementDocumentMutation,
  useRestoreAgreementDocumentMutation,
  useUploadAgreementDocumentMutation,
} from '~/modules/query/mlsAgreements';

import type { DocumentRow } from '~/components/pages/mls-agreements/types';

interface DocumentsPanelProps {
  entityType: string;
  entityId: string;
  documents?: DocumentRow[];
  showDeleted?: boolean;
}

// Shared document list + upload, used by the MLS and membership pages.
// `documents` comes from the parent detail query (mls/:id or memberships/:id) — there is no
// standalone document list route, so the upload/delete/restore mutations invalidate the parent
// detail and it refetches.
export const DocumentsPanel = ({
  entityType,
  entityId,
  documents = [],
  showDeleted = false,
}: DocumentsPanelProps) => {
  const [docType, setDocType] = useState(DOC_TYPES[0]);
  const [hasCredentials, setHasCredentials] = useState(false);
  const [deleting, setDeleting] = useState<DocumentRow | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const [uploadDocument, { isLoading: uploading }] = useUploadAgreementDocumentMutation();
  const [downloadDocument] = useDownloadAgreementDocumentMutation();
  const [deleteDocument] = useDeleteAgreementDocumentMutation();
  const [restoreDocument] = useRestoreAgreementDocumentMutation();

  const visible = documents.filter((d) => showDeleted || !d.deletedAt);

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      await uploadDocument({ file, entityType, entityId, docType, hasCredentials }).unwrap();
      toast.success('Document uploaded');
    } catch (err) {
      toast.error(errorText(err, 'The document could not be uploaded. Try again.'));
    }
  };

  const download = async (doc: DocumentRow) => {
    try {
      const blob = await downloadDocument(doc.id as string).unwrap();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = doc.filename || 'document';
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(errorText(err, 'The document could not be downloaded. Try again.'));
    }
  };

  const remove = (doc: DocumentRow) => {
    deleteDocument(doc.id as string)
      .unwrap()
      .then(() => toast.success('Document deleted'))
      .catch((err) => toast.error(errorText(err, 'The document could not be deleted. Try again.')));
  };

  const restore = (doc: DocumentRow) => {
    restoreDocument(doc.id as string)
      .unwrap()
      .then(() => toast.success('Document restored'))
      .catch((err) =>
        toast.error(errorText(err, 'The document could not be restored. Try again.')),
      );
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Select
          className="w-40"
          id="mls-agreements-doc-type"
          testId="mls-agreements-doc-type"
          ariaLabel="Document type"
          options={toOptions(DOC_TYPES)}
          value={docType}
          onChange={setDocType}
        />
        <ToggleField
          inline
          id="mls-agreements-doc-credentials"
          text="Contains credentials"
          checked={hasCredentials}
          onChange={setHasCredentials}
          testId="mls-agreements-doc-credentials"
        />
        <Button
          variant="outline"
          size="sm"
          startIcon={<Upload className="size-4" aria-hidden="true" />}
          loading={uploading}
          onClick={() => fileInput.current?.click()}
          testId="mls-agreements-doc-upload"
        >
          {uploading ? 'Uploading…' : 'Upload'}
        </Button>
        <input
          ref={fileInput}
          type="file"
          className="hidden"
          onChange={onFile}
          disabled={uploading}
          aria-label="Document file"
        />
      </div>
      <PanelTable<DocumentRow>
        ariaLabel="Documents"
        testId="mls-agreements-documents"
        emptyMessage="No documents yet."
        emptyDescription="Upload one to attach it to this record."
        data={visible}
        getRowId={(d, i) => d.id ?? i}
        columns={[
          {
            header: 'File',
            render: (doc) => (
              <>
                {doc.filename}
                {doc.deletedAt ? (
                  <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">(deleted)</span>
                ) : null}
              </>
            ),
          },
          {
            header: 'Type',
            render: (doc) => (
              <>
                {doc.docType}
                {doc.hasCredentials ? (
                  <Badge size="sm" color="warning" className="ml-2">
                    Credentials
                  </Badge>
                ) : null}
              </>
            ),
          },
          {
            header: 'Actions',
            width: 'w-32',
            render: (doc) => (
              <div className="flex gap-1">
                <Button
                  size="icon"
                  variant="ghost"
                  ariaLabel={`Download ${doc.filename}`}
                  title="Download"
                  onClick={() => download(doc)}
                >
                  <Download className="size-4" aria-hidden="true" />
                </Button>
                {!doc.deletedAt && (
                  <Button
                    size="icon"
                    variant="ghost"
                    ariaLabel={`Delete ${doc.filename}`}
                    title="Delete"
                    onClick={() => setDeleting(doc)}
                  >
                    <Trash2 className="size-4" aria-hidden="true" />
                  </Button>
                )}
                {doc.deletedAt && (
                  <Button
                    size="icon"
                    variant="ghost"
                    ariaLabel={`Restore ${doc.filename}`}
                    title="Restore"
                    onClick={() => restore(doc)}
                  >
                    <RotateCcw className="size-4" aria-hidden="true" />
                  </Button>
                )}
              </div>
            ),
          },
        ]}
      />
      <ConfirmModal
        isOpen={!!deleting}
        title="Delete document"
        message={`Delete '${deleting?.filename}'?`}
        confirmLabel="Delete"
        variant="danger"
        dangerNote="It leaves this list, and Toolbox has no way to bring it back."
        testId="mls-agreements-document-delete"
        onClose={() => setDeleting(null)}
        onConfirm={() => {
          if (deleting) remove(deleting);
          setDeleting(null);
        }}
      />
    </div>
  );
};

export default DocumentsPanel;
